import _ from 'underscore';
import React, { useCallback, useContext, useState, useEffect, useMemo } from 'react';
import { Text } from '@fluentui/react';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import PropTypes from 'prop-types';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { LegacyBudget } from '@bingads-webui-campaign-react/legacy-budget';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { BudgetSuggestionDialog, useBudgetSuggestionRepository, MODE } from '@bingads-webui-campaign-react/budget-suggestion-v2';
import { CampaignTypes } from '@bingads-webui-campaign-react/constants';
// eslint-disable-next-line
import _bingads_webui_static_legacy_page_context from '@bingads-webui-static-legacy/page-context';
// eslint-disable-next-line
const { BudgetRange } = _bingads_webui_static_legacy_page_context;

const LegacyBudgetWithCustomValidation = ({
  campaignId, showBudgetSuggestion, isCampaignSetting, currency, validationProps, updateValidation, campaign, isPremiumStreaming, ...restProps
}) => {
  const { data } = validationProps;
  const i18n = useContext(I18nContext);

  const activity = useContext(ActivityContext);

  const { DailyRange } = BudgetRange || {};

  const budgetSuggestion = useMemo(() => ({
    Currency: currency,
    GlobalMaximumBudget: { DailyBudget: DailyRange.CeilingValue ? DailyRange.CeilingValue : 3000000 },
    GlobalMinimumBudget: { DailyBudget: DailyRange.FloorValue ? DailyRange.FloorValue : 0.05 },
  }), [currency, DailyRange.CeilingValue, DailyRange.FloorValue]);

  const onChange = useCallback((newData, newIsValid) => {
    updateValidation(newIsValid ? [] : [i18n.getString(_TL_('Legacy Budget has validation errors'))], { Budget: newData });
    if (activity) {
      // signal activity to parent
      activity.signal(`onLegacyBudgetChange - ${JSON.stringify(newData.Amount)} - isValid: ${newIsValid}}`);
    }
  }, [i18n, updateValidation, activity]);

  const [budgetSuggestionData, setBudgetSuggestionData] = useState(null);
  const {
    loadBudget,
  } = useBudgetSuggestionRepository({
    campaignId,
    mode: MODE.EDITING,
    campaignType: CampaignTypes.PerformanceMax,
    currency,
  });


  useEffect(() => {
    if (showBudgetSuggestion && isCampaignSetting) {
      loadBudget({ campaign: {}, assetGroup: {} }).then((datas) => {
        setBudgetSuggestionData(datas);
      }).finally(() => {
      });
    }
  }, [isCampaignSetting, loadBudget, showBudgetSuggestion]);

  return (
    <div>
      <div className="budget-title-container" style={{ marginBottom: 10, fontWeight: 600 }}>
        <Text>{i18n.getString(_TL_('Daily budget'))}</Text>
        <HelpPopup
          topic={isPremiumStreaming ? 'pop_BA_PremiumStreaming_Budget' : 'pop_BA_CampaignCreate_Budget_SharedBudget'}
          aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: i18n.getString(_TL_('Campaign budget')) })}
        />
      </div>
      <LegacyBudget
        level="campaign"
        campaignId={campaignId}
        onChange={onChange}
        budget={data.Budget}
        {...restProps}
        i18n={i18n}
      />

      {/* will check Delivery Status in campaign when MT added */}
      {/* {showBudgetSuggestion && (data.DeliveryStatus === 'BudgetSuggestions' || 'BudgetSuggestions' in data.DeliveryStatusDetails) && isCampaignSetting && */}
      {showBudgetSuggestion &&
      isCampaignSetting &&
      !_.isEmpty(budgetSuggestionData) &&
      <BudgetSuggestionDialog
        campaignId={campaignId}
        budget={data.Budget}
        currency={currency}
        onBudgetChange={onChange}
        i18n={i18n}
        activity={activity}
        campaign={campaign.data}
        minBudget={budgetSuggestion.GlobalMinimumBudget.DailyBudget}
        maxBudget={budgetSuggestion.GlobalMaximumBudget.DailyBudget}
        {...restProps}
      />
     }
    </div>

  );
};

LegacyBudgetWithCustomValidation.propTypes = {
  campaignId: PropTypes.string.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      Budget: PropTypes.shape({
        Amount: PropTypes.number,
      }),
      DeliveryStatus: PropTypes.string,
      DeliveryStatusDetails: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  showBudgetSuggestion: PropTypes.bool,
  isCampaignSetting: PropTypes.bool,
  isPremiumStreaming: PropTypes.bool,
  currency: PropTypes.string,
  campaign: PropTypes.shape({
    data: PropTypes.shape({
    }),
  }),
};

LegacyBudgetWithCustomValidation.defaultProps = {
  showBudgetSuggestion: false,
  isCampaignSetting: false,
  isPremiumStreaming: false,
  currency: 'USD',
  campaign: {
    data: {},
  },
};

export const ValidatedLegacyBudget = withCustomValidation(LegacyBudgetWithCustomValidation);

